/**
 * Tool Approve/Reject card (frontend-ux-design.md §4.3 E2).
 *
 * The ONE place the Assistant rail uses a real border — a mutating action must
 * read as a deliberate, bounded decision. `bg-card border rounded-md shadow-sm`,
 * primary `Approve` (filled accent), `Reject` (`variant="outline"`).
 *
 * SHELL: the buttons mark the card resolved locally; the LATER slice maps them
 * onto `addToolApprovalResponse({ id, approved })`.
 */
import { Button } from "@/components/ui/button";
import { Icon } from "@/components/ui/icon";
import { JsonViewer } from "@/components/app/JsonViewer";
import type { AssistantToolCallPart } from "@/components/assistant/types";

interface ToolApprovalCardProps {
  part: AssistantToolCallPart;
  onApprove: (toolCallId: string) => void;
  onReject: (toolCallId: string) => void;
}

export function ToolApprovalCard({
  part,
  onApprove,
  onReject,
}: ToolApprovalCardProps) {
  const resolved = part.state !== "approval-requested";

  return (
    <div
      data-testid="assistant-tool-approval"
      className="rounded-md border border-border bg-card p-3 shadow-sm"
    >
      <div className="flex items-start gap-2">
        <span className="mt-0.5 shrink-0 text-muted-foreground">
          <Icon name="tool" size={16} aria-hidden />
        </span>
        <div className="min-w-0 flex-1 space-y-1">
          <p className="font-mono text-xs text-muted-foreground">
            {part.toolName}
          </p>
          <p className="text-sm font-medium">{part.summary}</p>
        </div>
      </div>

      <div className="mt-2">
        <JsonViewer data={part.args} />
      </div>

      {resolved ? (
        <p className="mt-3 text-xs font-medium text-muted-foreground">
          {part.state === "approved" ? "Approved" : "Rejected"}
        </p>
      ) : (
        <>
          <div className="mt-3 flex items-center gap-2">
            <Button
              size="sm"
              disabled={part.pending}
              data-testid="assistant-tool-approve"
              onClick={() => onApprove(part.toolCallId)}
            >
              <Icon name="approve" size={16} aria-hidden />
              Approve
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={part.pending}
              data-testid="assistant-tool-reject"
              onClick={() => onReject(part.toolCallId)}
            >
              <Icon name="reject" size={16} aria-hidden />
              Reject
            </Button>
          </div>
          {part.error && (
            <p
              className="mt-2 text-xs font-medium text-destructive"
              data-testid="assistant-tool-error"
            >
              {part.error}
            </p>
          )}
        </>
      )}
    </div>
  );
}

export default ToolApprovalCard;
